import NotificationsPausedIcon from "@mui/icons-material/NotificationsPaused";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { formatDistanceToNow } from "date-fns";
import { useSnackbar } from "notistack";
import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useApiMutation } from "../hooks/use-api-mutation";
import type { Issue } from "../types";
import {
  getIssueSeverityColor as getSeverityColor,
  getIssueStatusColor as getStatusColor,
  getIssueTypeColor as getTypeColor,
} from "../utils/issue-colors";
import { clickableProps } from "../utils/keyboard";
import { humanizeText } from "../utils/text";
import { DataGrid } from "./data-grid";

interface IssueActionColumnProps extends GridRenderCellParams<Issue> {
  onDelete?: () => void;
}

const IssueActionColumn = ({ row, onDelete }: IssueActionColumnProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [ignoreConfirmOpen, setIgnoreConfirmOpen] = useState(false);

  const { mutate: ignoreIssue, isPending: isIgnoringIssue } = useApiMutation<
    { issue_status: string },
    void
  >(`PATCH`, `/issues/${row.id}`, {
    onSuccess: () => {
      enqueueSnackbar("Issue ignored successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      onDelete?.();
      setIgnoreConfirmOpen(false);
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to ignore issue: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  // Only show ignore action for open or reopened issues
  const canIgnore = row.issue_status === "open" || row.issue_status === "reopened";

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
      {canIgnore && (
        <Tooltip title="Ignore issue">
          <IconButton
            aria-label="ignore"
            onClick={(e) => {
              e.stopPropagation();
              setIgnoreConfirmOpen(true);
            }}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <NotificationsPausedIcon />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={ignoreConfirmOpen} onClose={() => setIgnoreConfirmOpen(false)}>
        <DialogTitle>Ignore Issue</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to ignore issue "{row.title}"? This will mark the issue as closed
            and stop notifications for it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => ignoreIssue({ issue_status: "closed" })}
            sx={{ color: "text.secondary" }}
            disabled={isIgnoringIssue}
          >
            Ignore
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

interface IssueTableProps {
  data: Issue[];
  isLoading?: boolean;
  onDelete?: () => void;
  height?: number | string;
}

export const IssueTable: React.FC<IssueTableProps> = ({
  data,
  isLoading = false,
  onDelete,
  height = "calc(100% - 100px)",
}) => {
  const nav = useNavigate();

  const handleRowClick = (params: GridRowParams<Issue>) => {
    nav(`/issues/${params.row.id}`);
  };

  const columns = useMemo<GridColDef<Issue>[]>(() => {
    const cols: GridColDef<Issue>[] = [
      {
        field: "slug",
        headerName: "ID",
        width: 120,
        renderCell: (params) => (
          <Typography
            variant="caption"
            sx={{
              fontFamily: "monospace",
              height: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        minWidth: 320,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "issue_status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => (
          <Chip
            label={humanizeText(params.value)}
            color={getStatusColor(params.value)}
            size="small"
            variant="filled"
          />
        ),
      },
      {
        field: "severity",
        headerName: "Severity",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={humanizeText(params.value)}
            color={getSeverityColor(params.value)}
            size="small"
            variant={["critical", "high"].includes(params.value) ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "issue_type",
        headerName: "Type",
        width: 140,
        renderCell: (params) => (
          <Chip
            label={humanizeText(params.value)}
            color={getTypeColor(params.value)}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "seen_count",
        headerName: "Count",
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "url",
        headerName: "URL",
        width: 140,
        renderCell: (params) => {
          if (!params.value) return "";

          try {
            new URL(params.value);
          } catch {
            return "-";
          }

          return (
            <Typography
              variant="body2"
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
                height: "100%",
                display: "flex",
                alignItems: "center",
              }}
              {...clickableProps(() => window.open(params.value, "_blank"))}
              onClick={() => window.open(params.value, "_blank")}
            >
              {new URL(params.value).pathname}
            </Typography>
          );
        },
      },
      {
        field: "last_seen",
        headerName: "Last Seen",
        width: 140,
        renderCell: (params) => {
          if (!params.value) return "-";
          return formatDistanceToNow(new Date(params.value), {
            addSuffix: true,
          });
        },
      },
    ];

    cols.push({
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => <IssueActionColumn {...params} onDelete={onDelete} />,
    });

    return cols;
  }, [onDelete]);

  return (
    <Box sx={{ height: height, width: "100%" }}>
      <DataGrid
        rows={data}
        columns={columns}
        loading={isLoading}
        onRowClick={handleRowClick}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          sorting: {
            sortModel: [{ field: "last_seen", sort: "desc" }],
          },
        }}
      />
    </Box>
  );
};
